import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import io from 'socket.io-client';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';

// Components
import DashboardHome from '@/components/superadmin/DashboardHome';
import CompaniesPage from '@/components/superadmin/CompaniesPage';
import PartnersPage from '@/components/superadmin/PartnersPage';
import ReportsPage from '@/components/superadmin/ReportsPage';
import SettingsPage from '@/components/superadmin/SettingsPage';
import PricingPage from '@/components/superadmin/PricingPage';
import TeamsPage from '@/components/superadmin/TeamsPage';
import PaymentSettingsPage from '@/components/superadmin/PaymentSettingsPage';
import MailerPage from '@/components/superadmin/MailerPage';
import PartnerPayoutsPage from '@/components/superadmin/PartnerPayoutsPage';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

// UI Components
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Building2, 
  FileText, 
  Settings,
  LogOut, 
  Menu,
  X,
  DollarSign,
  Users,
  CreditCard,
  Handshake,
  Mail,
  Banknote
} from 'lucide-react';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { branding, refreshBranding } = useBranding();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    // Get user info
    fetchUser();
    
    // Setup WebSocket connection
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const newSocket = io(BACKEND_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      upgrade: true,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      timeout: 20000,
    });
    
    newSocket.on('connect', () => {
      console.log('WebSocket connected');
      newSocket.emit('subscribe_dashboard', {});
    });
    
    newSocket.on('dashboard_update', (data) => {
      console.log('Dashboard update received:', data);
      // Trigger dashboard refresh
      window.dispatchEvent(new CustomEvent('dashboard_update'));
    });
    
    setSocket(newSocket);
    
    return () => {
      if (newSocket) newSocket.disconnect();
    };
  }, []);
  
  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/guru');
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/guru');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const allNavItems = [
    { path: '/superadmin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), access: ['all'] },
    { path: '/superadmin/companies', icon: Building2, label: t('nav.companies'), access: ['all'] },
    { path: '/superadmin/partners', icon: Handshake, label: t('nav.partners'), access: ['superadmin'] },
    { path: '/superadmin/partner-payouts', icon: Banknote, label: 'Partner Payouts', access: ['superadmin', 'finance'] },
    { path: '/superadmin/teams', icon: Users, label: t('nav.teams'), access: ['superadmin'] },
    { path: '/superadmin/pricing', icon: DollarSign, label: t('nav.pricing'), access: ['superadmin'] },
    { path: '/superadmin/payments', icon: CreditCard, label: t('nav.payments'), access: ['superadmin', 'finance'] },
    { path: '/superadmin/reports', icon: FileText, label: t('nav.reports'), access: ['all'] },
    { path: '/superadmin/mailer', icon: Mail, label: t('nav.mailer'), access: ['superadmin'] },
    { path: '/superadmin/settings', icon: Settings, label: t('nav.settings'), access: ['superadmin'] },
  ];
  
  const memberType = user?.member_type || 'superadmin';
  const navItems = allNavItems.filter(item => 
    item.access.includes('all') || item.access.includes(memberType)
  );
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-40
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={branding.platform_name} 
                  className={`transition-all ${sidebarOpen ? 'h-8' : 'h-6'} object-contain`}
                />
              ) : (
                <>
                  <Building2 className={`text-indigo-600 ${sidebarOpen ? 'w-6 h-6' : 'w-5 h-5'}`} />
                  <h1 className={`font-bold text-indigo-600 transition-all ${sidebarOpen ? 'text-lg' : 'hidden'}`}>
                    {branding.platform_name}
                  </h1>
                </>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2" data-testid="navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-100'}
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {sidebarOpen && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>
          
          {/* User Menu */}
          <div className="p-4 border-t border-gray-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('common.details')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Top Header Bar */}
      <header className={`
        fixed top-0 right-0 z-30 bg-white border-b border-gray-200 h-14
        transition-all duration-300
        ${sidebarOpen ? 'lg:left-64' : 'lg:left-20'}
        left-0
      `}>
        <div className="flex items-center justify-between h-full px-4">
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LanguageSelector compact />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`
        transition-all duration-300
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        mt-14 p-8
      `}>
        <Routes>
          <Route path="dashboard" element={<DashboardHome socket={socket} />} />
          <Route path="companies" element={<CompaniesPage currentUser={user} />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="partner-payouts" element={<PartnerPayoutsPage />} />
          <Route path="teams" element={<TeamsPage currentUser={user} />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="payments" element={<PaymentSettingsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="mailer" element={<MailerPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<DashboardHome socket={socket} />} />
        </Routes>
      </main>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}

export default SuperAdminDashboard;
